import * as React from 'react'

import { useErrorContext } from 'packages/common'
import { HkEmployeeType } from 'packages/grimoire'
import { createDateString } from 'packages/utils/dateHelpers'
import { useMemoById, useOnlineStatus } from 'packages/utils/hooks'
import { logError } from 'packages/wiretap/logging'

import { Slugs, useI18n } from 'app/fieldapp/i18n'
import { Assignment } from 'app/fieldapp/store/assignments'
import { Clean, CleansResponse } from 'app/fieldapp/store/cleans'
import { CleanTimesResponse } from 'app/fieldapp/store/cleantimes'
import { OtherTimersResponse } from 'app/fieldapp/store/otherTimers'
import { TicketTimesResponse } from 'app/fieldapp/store/ticket-times/ticket-times.types'
import { TicketsResponse } from 'app/fieldapp/store/tickets'
import { User } from 'app/fieldapp/store/users'
import { useOfflineData } from 'app/fieldapp/store/utils/useOfflineData'
import { Visit, VisitsResponse } from 'app/fieldapp/store/visits'
import { useTimelineDateRange } from 'app/fieldapp/utils/timelineHelpers'

import { useAppContext } from '../AppContext'
import { usePrivacyDisclosureModal } from '../PrivacyDisclosureModal'

export type FieldAppInitializerProps = {
  fetchCleanTimes: () => Promise<CleanTimesResponse>
  fetchCleans: (
    userId: string,
    startDate: string,
    endDate?: string,
  ) => Promise<CleansResponse>
  fetchOtherTimers: () => Promise<OtherTimersResponse>
  fetchTicketTimes: () => Promise<TicketTimesResponse>
  fetchTickets: (userId: string) => Promise<TicketsResponse>
  fetchVisits: (userId: string, date: string) => Promise<VisitsResponse>
  getActiveUser: () => User | undefined
  getActiveUserAssignmentByClean: (clean: Clean) => Assignment | undefined
  getAuthUser: () => User | undefined
  getFrozenCleans: () => Clean[]
  getVisits: () => Visit[]
}

export const FieldAppInitializer: React.FC<FieldAppInitializerProps> =
  React.memo(
    ({
      getActiveUser,
      getActiveUserAssignmentByClean,
      getAuthUser,
      getFrozenCleans,
      getVisits,
      fetchCleans,
      fetchCleanTimes,
      fetchOtherTimers,
      fetchTicketTimes,
      fetchTickets,
      fetchVisits,
    }) => {
      usePrivacyDisclosureModal()

      const { isRefetchingAppData, onAppDataLoaded, setIsRefetchingAppData } =
        useAppContext()

      const isOnline = useOnlineStatus().isOnline()
      const { reloadOfflineData } = useOfflineData()
      const { setBannerError } = useErrorContext()
      const { t } = useI18n()

      const activeUser = useMemoById(getActiveUser())
      const authUser = useMemoById(getAuthUser())
      const frozenCleans = getFrozenCleans()
      const visits = getVisits()
      const { startDate, endDate } = useTimelineDateRange()

      const [refetchDependentData, setRefetchDependentData] =
        React.useState(false)

      const userId = activeUser?.id

      const fetchMainData = React.useCallback((): void => {
        if (!userId) return

        const fetchMainDataForUser = async () => {
          try {
            await Promise.all([
              fetchCleans(userId, startDate, endDate),
              fetchTickets(userId),
            ])

            // we need to make the visits request separately to avoid data collisions with the fetchCleans request
            await fetchVisits(userId, createDateString())

            setRefetchDependentData(true)
          } catch (err) {
            setBannerError({ message: t(Slugs.anErrorOccurredLong) })
            logError(err)
          }
        }

        fetchMainDataForUser()
      }, [
        endDate,
        fetchCleans,
        fetchTickets,
        fetchVisits,
        setBannerError,
        startDate,
        t,
        userId,
      ])

      /**
       * Effect handler to re-fetch all "dependent" data. This data all pertains to
       * relationships from the data fetch in "fetchMainData," so it has to be done
       * after those requests have all successfully completed.
       */
      React.useEffect(() => {
        /*
         * The dependent fetching logic looks like this:
         * - Always fetch otherTimers and ticketTimes, as they have no further dependencies
         * - When we have cleans in the freeze window:
         *    - Always fetch cleanTimes
         *    - Fetch lockbox/smartLock codes ONLY when we are online
         *        (these cannot be cached for security reasons)
         *
         * Note that any requests that can be triggered offline must be registered
         * as cacheable requests in the ServiceWorker config.
         */
        const asyncFetchDependentData = async () => {
          if (!userId) return

          try {
            const promises: Promise<unknown>[] = [
              fetchOtherTimers(),
              fetchTicketTimes(),
            ]

            const frozenCleanIds = frozenCleans.map(clean => clean.id)
            if (
              (frozenCleanIds.length || !!visits.length) &&
              activeUser?.employeeType === HkEmployeeType.employee
            ) {
              promises.push(fetchCleanTimes())
            }

            await reloadOfflineData()
            await Promise.all(promises)
            onAppDataLoaded()
          } catch (err) {
            setBannerError({ message: t(Slugs.anErrorOccurredLong) })
            logError(err)
          }
        }

        if (refetchDependentData) {
          setRefetchDependentData(false)
          asyncFetchDependentData()
        }
      }, [
        activeUser,
        fetchCleanTimes,
        fetchOtherTimers,
        fetchTicketTimes,
        frozenCleans,
        getActiveUserAssignmentByClean,
        isOnline,
        onAppDataLoaded,
        refetchDependentData,
        reloadOfflineData,
        setBannerError,
        t,
        userId,
        visits.length,
      ])

      /** When auth/user changes, begin refetching all essential data */
      React.useEffect(() => {
        if (activeUser && authUser) {
          setIsRefetchingAppData(true)
        }
      }, [activeUser, authUser, setIsRefetchingAppData])

      /** When the "refetching" flag  changes, trigger a re-fetch of all essential data */
      React.useEffect(() => {
        if (isRefetchingAppData) {
          fetchMainData()
        }
      }, [fetchMainData, isRefetchingAppData])

      return null
    },
  )
